import React from 'react';
import { ArrowLeft, ArrowRight, BookOpen, Bookmark, Lightbulb } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { APPENDIX_CONTAINERS } from '../components/AppendixPageLayout';

/* Hero image — kept per user preference (image 5 — documents/sunburst, warm palette). */
const heroImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/4ewcpbrr_5.png';

const descriptions = {
  'error-codes':    'Complete list of all possible error codes and their meanings. Essential for implementing proper error handling and providing meaningful feedback to users.',
  'url-encoding':   'Guidelines for properly encoding URLs and parameters in API requests. Ensures data integrity and prevents transmission errors.',
  'country-codes':  'Complete list of supported country codes for address validation, compliance and reporting requirements.',
  'currency-codes': 'Comprehensive list of supported currency codes and formats for multi-currency processing and international transactions.',
  'test-cards':     'Safe test card numbers for development and testing environments. Includes cards for testing various scenarios, responses and edge cases.',
};

const taglines = {
  'error-codes':    'Complete error code reference',
  'url-encoding':   'Character encoding specifications',
  'country-codes':  'ISO country code reference',
  'currency-codes': 'Supported currency reference',
  'test-cards':     'Development & testing card numbers',
};

const Appendix = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="appendix-landing-page">
      <Header />
      <div className="h-20" />

      <section
        className="relative flex-1 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
          <button
            onClick={() => navigate('/technical-documentation')}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="appendix-back-button"
          >
            <ArrowLeft size={18} />
            Back to Technical Documentation
          </button>

          {/* Intro — black text as requested */}
          <div className="mb-10 max-w-4xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-black text-[11px] font-bold uppercase tracking-[0.15em] mb-5"
              data-testid="appendix-eyebrow"
            >
              <Bookmark size={13} />
              Quick Reference Guide
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-5 leading-tight" data-testid="appendix-title">
              Appendix
            </h1>
            <p className="text-lg text-black leading-relaxed max-w-3xl font-bold">
              The Appendix contains essential reference materials to support your API integration and testing processes. From error code interpretations to test card numbers, these resources provide the detailed information you need for successful implementation and troubleshooting.
            </p>
          </div>

          {/* Two info cards — glass morphism matching Notifications landing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 max-w-5xl">
            <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/15 p-6" data-testid="appendix-qrg-card">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={18} className="text-[#ef5b25]" />
                <h2 className="text-lg font-bold text-white">Quick Reference Guide</h2>
              </div>
              <p className="text-sm text-white/75 leading-relaxed">
                This section serves as your go-to resource for codes, standards and testing materials. Keep these references handy during development and testing phases to streamline your integration process.
              </p>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/15 p-6" data-testid="appendix-how-to-card">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb size={18} className="text-[#ef5b25]" />
                <h2 className="text-lg font-bold text-white">How to Use</h2>
              </div>
              <p className="text-sm text-white/75 leading-relaxed">
                Each section provides comprehensive tables and lists that you can reference during development, testing and live operations. Bookmark the sections most relevant to your integration for quick access.
              </p>
            </div>
          </div>

          {/* 5 Containers — match Products page layout: 3-col grid with side card spanning 2 rows */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="appendix-topic-grid">
            {/* Row 1, Col 1: Error Codes */}
            {/* Row 1, Col 2: URL Encoding */}
            {/* Col 3 (spans rows 1-2, centered): Test Cards */}
            {/* Row 2, Col 1: Country Codes */}
            {/* Row 2, Col 2: Currency Codes */}
            {['error-codes', 'url-encoding', 'test-cards', 'country-codes', 'currency-codes'].map((id) => {
              const c = APPENDIX_CONTAINERS.find((x) => x.id === id);
              const isSideCard = c.id === 'test-cards';
              return (
                <Link
                  key={c.id}
                  to={c.link}
                  className={`group relative overflow-hidden bg-white/95 backdrop-blur-sm rounded-lg shadow-xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ${
                    isSideCard ? 'mobile-last md:row-span-2 md:self-center md:h-fit' : ''
                  }`}
                  data-testid={`appendix-card-${c.id}`}
                >
                  <div className="absolute inset-x-0 top-0 h-1 bg-[#1F4E79]" />
                  <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full blur-2xl bg-gradient-to-br from-[#ef5b25]/20 to-[#1F4E79]/15 opacity-60 pointer-events-none" />

                  <div className="relative">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">{taglines[c.id]}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1F4E79] transition-colors">{c.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4">{descriptions[c.id]}</p>
                    <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-xs group-hover:gap-2 transition-all">
                      Open reference
                      <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <p className="text-xs text-white/65 mt-8 max-w-3xl" data-testid="appendix-tip">
            <strong className="text-white/85">Tip:</strong> Press{' '}
            <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/25 rounded text-[10px] text-white font-sans">⌘</kbd>/
            <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/25 rounded text-[10px] text-white font-sans">Ctrl</kbd>+
            <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/25 rounded text-[10px] text-white font-sans">K</kbd>{' '}
            anywhere in the docs to jump directly to any of these tables (e.g. "0027", "EUR", "test card").
          </p>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Appendix;
